import React, { useState, useEffect, useCallback } from 'react';
import CommonModalForm from '../../components/commonComponent/CommonModalForm';
import { validateForm } from '../../utils/formUtils';
import { toastSuccess, toastError } from '../../utils/alertHelper';
import axios from '../../utils/axiosInstance';
import { FaEdit, FaTrash } from 'react-icons/fa';

const CityPage = () => {
  const [cities, setCities] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const [formValues, setFormValues] = useState({
    name: '',
    country: '',
    state: '',
    cityImage: null, // ✅ image param
  });

  const [originalImage, setOriginalImage] = useState(null);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const limit = parseInt(process.env.REACT_APP_PAGE_LIMIT || 10);

  /* ===================== FETCH DATA ===================== */

  const fetchCountries = async () => {
    try {
      const res = await axios.get('/admin/api/country');
      setCountries(res.data.countries || []);
    } catch {
      setCountries([]);
    }
  };

  const fetchStates = async (countryId) => {
    try {
      const res = await axios.get(`/admin/api/state?country=${countryId}`);
      setStates(res.data.states || []);
    } catch {
      setStates([]);
    }
  };

  const fetchCities = useCallback(async (page = 1, search = '') => {
    try {
      setLoading(true);
      const res = await axios.get(
        `/admin/api/city?page=${page}&limit=${limit}&search=${search}`
      );

      const { cities = [], totalCount = 0 } = res.data;
      setCities(cities);
      setTotalPages(Math.ceil(totalCount / limit));
    } catch (err) {
      toastError('Failed to fetch cities');
    } finally {
      setLoading(false);
    }
  }, [limit]);

  /* ===================== EFFECTS ===================== */

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 3 || searchQuery.length === 0) {
        setDebouncedSearchQuery(searchQuery);
        setCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    fetchCities(currentPage, debouncedSearchQuery);
  }, [currentPage, debouncedSearchQuery, fetchCities]);

  useEffect(() => {
    fetchCountries();
  }, []);

  useEffect(() => {
    if (formValues.country) {
      fetchStates(formValues.country);
    } else {
      setStates([]);
      setFormValues((prev) => ({ ...prev, state: '' }));
    }
  }, [formValues.country]);

  /* ===================== SAVE ===================== */

  const handleSave = async () => {
    const validationErrors = validateForm(formValues, {
      name: { required: true },
      country: { required: true },
      state: { required: true },
    });

    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }

    try {
      setSaving(true);
      const formData = new FormData();

      formData.append('name', formValues.name);
      formData.append('country', formValues.country);
      formData.append('state', formValues.state);

      // ✅ cityImage param used
      if (formValues.cityImage instanceof File) {
        formData.append('cityImage', formValues.cityImage);
      } else if (editingId && originalImage) {
        formData.append('removeCityImage', '1');
      }

      if (editingId) {
        await axios.put(`/admin/api/city/${editingId}`, formData);
        toastSuccess('City updated successfully!');
      } else {
        await axios.post('/admin/api/city', formData);
        toastSuccess('City added successfully!');
      }

      setCurrentPage(1);
      fetchCities(1, searchQuery);
      handleCancel();
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to save city');
    } finally {
      setSaving(false);
    }
  };

  /* ===================== EDIT / DELETE ===================== */

  const handleEdit = (city) => {
    setFormValues({
      name: city.name || '',
      country: city.country || '',
      state: city.state || '',
      cityImage: null,
    });
    setOriginalImage(city.cityImage || null);
    setEditingId(city._id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure?')) return;

    try {
      await axios.delete(`/admin/api/city/${id}`);
      toastSuccess('City deleted successfully!');
      fetchCities(1, searchQuery);
    } catch {
      toastError('Failed to delete city');
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setFormValues({ name: '', country: '', state: '', cityImage: null });
    setEditingId(null);
    setErrors({});
    setOriginalImage(null);
  };

  /* ===================== UI ===================== */

  return (
    <div className="container mt-2">
      <div className="d-flex justify-content-between mb-3">
        <h3>Cities</h3>
        <input
          className="form-control w-25"
          placeholder="Search (min 3 chars)"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between">
          <h5>City List</h5>
          <button className="btn btn-sm btn-success" onClick={() => setShowModal(true)}>
            + Add City
          </button>
        </div>

        <div className="table-responsive">
          <table className="table table-bordered table-sm mb-0">
            <thead>
              <tr>
                <th>#</th>
                <th>Image</th>
                <th>Name</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="4" className="text-center">Loading...</td>
                </tr>
              ) : cities.length ? (
                cities.map((city, i) => (
                  <tr key={city._id}>
                    <td>{(currentPage - 1) * limit + i + 1}</td>
                    <td>
                      <img
                        src={city.image || '/assets/images/default.png'}
                        alt={city.name}
                        width="60"
                        height="40"
                        style={{ objectFit: 'cover' }}
                      />
                    </td>
                    <td>{city.name}</td>
                    <td>
                      <button className="btn btn-sm" onClick={() => handleEdit(city)}>
                        <FaEdit />
                      </button>
                      <button className="btn btn-sm text-danger" onClick={() => handleDelete(city._id)}>
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center">No data found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <CommonModalForm
        visible={showModal}
        title={editingId ? 'Edit City' : 'Add City'}
        fields={[
          {
            name: 'country',
            label: 'Country',
            type: 'select',
            options: countries.map(c => ({ value: c._id, label: c.name })),
          },
          {
            name: 'state',
            label: 'State',
            type: 'select',
            options: states.map(s => ({ value: s._id, label: s.name })),
            disabled: !formValues.country,
          },
          {
            name: 'cityImage',
            label: 'City Image',
            type: 'file',
            accept: 'image/*',
          },
          {
            name: 'name',
            label: 'City Name',
            type: 'text',
          },
        ]}
        values={formValues}
        errors={errors}
        onChange={setFormValues}
        onSubmit={handleSave}
        onCancel={handleCancel}
        imagePreview={originalImage}
        submitLabel={saving ? 'Saving...' : editingId ? 'Update' : 'Save'}
        disabled={saving}
      />
    </div>
  );
};

export default CityPage;
